import { cn } from '../lib/utils';

import { VButton } from './vButton';

type VFilterBarOption<T extends string> = {
  value: T;
  label: string;
};

type VFilterBarProps<T extends string> = {
  options: ReadonlyArray<VFilterBarOption<T>>;
  value: T;
  counts: Record<T, number>;
  onChange: (value: T) => void;
  /** Toegankelijke naam van de knoppengroep, bijv. "Filter documenten op status". */
  ariaLabel: string;
  className?: string;
};

/**
 * Segmented filter: één knop per optie met het aantal erachter. Puur
 * presentatie — de callsite bepaalt opties, aantallen en wat filteren betekent.
 */
function VFilterBar<T extends string>({
  options,
  value,
  counts,
  onChange,
  ariaLabel,
  className,
}: VFilterBarProps<T>) {
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className={cn(
        'inline-flex flex-wrap gap-1 rounded-lg border border-border bg-card p-1',
        className,
      )}
    >
      {options.map((option) => {
        const active = value === option.value;
        return (
          <VButton
            key={option.value}
            type="button"
            size="sm"
            variant="ghost"
            aria-pressed={active}
            className={cn(
              active && 'bg-neutral-200 font-semibold text-neutral-900 hover:bg-neutral-200',
            )}
            onClick={() => onChange(option.value)}
          >
            {option.label}
            <span
              className={cn(
                'ml-1 tabular-nums',
                !active && 'text-muted-foreground',
              )}
            >
              {counts[option.value]}
            </span>
          </VButton>
        );
      })}
    </div>
  );
}

export { VFilterBar };
export type { VFilterBarOption, VFilterBarProps };
